/**
 * Block 13 PR-G Phase 4A — Generic chip-segment (segmented control) primitive.
 *
 * Используется для: Language picker (kz.ru/kz.kk), Cost source («Из КП»/«Вручную»),
 * Warranty unit («месяцев»/«лет»), 3-option Row source («Из списка»/«Вручную»/«Из КП»
 * в Phase 4C).
 *
 * Visual: horizontal row of equal-width buttons (≥44px tap target) с selected
 * state накладывающимся на bg-ink + text-card (filled inversion), unselected
 * bg-card + text-ink + border-accent.
 *
 * **A11y:** `role="radiogroup"` wrapper + `role="radio"` + `aria-checked` на
 * каждой кнопке. Keyboard nav через Tab (не arrow keys — стандартный chip
 * pattern, не RadioGroup).
 *
 * **Не использует** `<input type="radio">` (как RadioCard) — ChipSegment это
 * mid-emphasis selector а не primary anketa choice, поэтому upgrade на native
 * form semantics избыточен. ARIA `radiogroup` достаточен для screen readers.
 *
 * Layout dimensions per P-044: min-height `min-h-tap` (44px). Per P-019
 * (вертикальные списки): ChipSegment — exception для compactных переключателей
 * 2-3 option, не replacement для вертикальных radio (RadioCard).
 *
 * Reference:
 *   - prototype `screen-2.13.2-contract.html` `Seg` component
 *   - design-system.md v0.6 chip pattern
 */

import { useId } from 'react';

export interface ChipSegmentOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export interface ChipSegmentProps<TValue extends string = string> {
  options: ReadonlyArray<ChipSegmentOption<TValue>>;
  value: TValue;
  onChange: (value: TValue) => void;
  /** Aria label на wrapper'е. Обязательно — описывает что выбирается. */
  ariaLabel: string;
  /** Optional explicit name для test-id / DOM attributes. */
  name?: string;
  /** Если true — disable all options (e.g. hydration pending). */
  disabled?: boolean;
}

export function ChipSegment<TValue extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
  name,
  disabled,
}: ChipSegmentProps<TValue>) {
  const reactId = useId();
  const groupName = name ?? `chip-segment-${reactId}`;

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={name ? `${name}-segment` : undefined}
      className="flex items-stretch rounded-xl overflow-hidden border-[1.5px] border-accent bg-card"
    >
      {options.map((opt, i) => {
        const active = opt.value === value;
        const isLast = i === options.length - 1;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.label}
            data-testid={`${groupName}-option-${opt.value}`}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={
              'flex-1 min-h-tap px-3 py-2 text-base font-semibold leading-tight ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
              'disabled:opacity-50 disabled:cursor-not-allowed active:opacity-90 ' +
              (active ? 'bg-ink text-card ' : 'bg-card text-ink ') +
              (isLast ? '' : 'border-r border-accent')
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
